"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import { EmailAgentInbox } from "./use-agents";
import { openOAuthPopup } from "@/lib/oauth/popup";
import { useTranslations } from "next-intl"


/**
 * Hook to manage inboxes for a specific email agent
 */
export function useAgentInboxes(agentId: string | undefined) {
  const tUi = useTranslations("Feedback.AgentsEmailUseAgentInboxes")

  const [inboxes, setInboxes] = useState<EmailAgentInbox[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated, user } = useAuth();

  const loadInboxes = useCallback(async () => {
    if (!isAuthenticated || !agentId) {
      // Don't set isLoading to false - keep loading until auth is ready
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/agents/email/${agentId}/inboxes`);
      const data = await response.json();

      if (response.ok) {
        setInboxes(data.inboxes || []);
      } else {
        console.error("[useAgentInboxes] API error:", response.status, data);
        setError(data.error || "Failed to load inboxes");
      }
    } catch (err) {
      console.error("[useAgentInboxes] Error loading agent inboxes:", err);
      setError(tUi("failedToLoadInboxes"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, agentId]);

  useEffect(() => {
    loadInboxes();
  }, [loadInboxes]);

  /**
   * Add an existing inbox to the agent
   * @param inboxId - The inbox UUID to add
   * @param syncFromDate - Date string (YYYY-MM-DD) to start syncing emails from. Defaults to today.
   */
  const addInbox = useCallback(
    async (inboxId: string, syncFromDate?: string): Promise<void> => {
      if (!agentId) throw new Error(tUi("noAgentID"));

      setIsAdding(true);
      setError(null);

      try {
        const response = await fetchWithAuth(`/api/agents/email/${agentId}/inboxes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            inbox_id: inboxId,
            sync_from_date: syncFromDate,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.inbox) {
            setInboxes((prev) => [...prev, data.inbox]);
          } else {
            await loadInboxes();
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to add inbox");
        }
      } finally {
        setIsAdding(false);
      }
    },
    [agentId, tUi, fetchWithAuth, loadInboxes]
  );

  /**
   * Remove an inbox from the agent
   */
  const removeInbox = useCallback(
    async (inboxId: string): Promise<void> => {
      if (!agentId) throw new Error(tUi("noAgentID"));

      setIsRemoving(inboxId);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `/api/agents/email/${agentId}/inboxes?inbox_id=${inboxId}`,
          { method: "DELETE" }
        );

        if (response.ok) {
          setInboxes((prev) => prev.filter((inbox) => inbox.id !== inboxId));
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to remove inbox");
        }
      } finally {
        setIsRemoving(null);
      }
    },
    [agentId, tUi, fetchWithAuth]
  );

  /**
   * Start OAuth flow to connect a new Google account and attach it to this agent
   */
  const connectNewInbox = useCallback(async () => {
    if (!agentId || !user) return;

    try {
      const response = await fetchWithAuth("/api/auth/google/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_uuid: user.uuid ?? user.user_id,
          attach_to_agent: agentId,
          // No return_to - using popup mode with BroadcastChannel
        }),
      });

      const data = await response.json();
      if (response.ok && data.auth_url) {
        openOAuthPopup({
          url: data.auth_url,
          name: "google_inbox_oauth",
          onMessage: (result) => {
            if (result.google_oauth === "connected") {
              void loadInboxes()
            }
          },
          errorKey: "google_oauth_error",
          onError: (err) => setError(`Google connection failed: ${err}`),
        });
      } else {
        throw new Error(data.error || "Failed to start Google OAuth");
      }
    } catch (err) {
      console.error("Error starting OAuth:", err);
      setError(err instanceof Error ? err.message : tUi("failedToConnect"));
    }
  }, [agentId, user, fetchWithAuth, loadInboxes, tUi]);

  const refreshInboxes = useCallback(loadInboxes, [loadInboxes]);

  return {
    inboxes,
    isLoading,
    isAdding,
    isRemoving,
    error,
    addInbox,
    removeInbox,
    connectNewInbox,
    refreshInboxes,
  };
}
